<template>
  <div class="view folder">
    <banner v-if="showAuthBanner" v-on:closeBanner="showAuthBanner = false"></banner>
    <div class="top-bar">
      <div class="home-btn">
        <img src="@/assets/icons/home.svg" alt="Home" @click="goBack">
      </div>
      <Card :item="folderItem" :hasHover="false" class="card--desktop-only" />
    </div>
    <div class="items-wrap">
      <Slider ref="slider" @scroll="onScroll">
        <Card :item="folderItem" :hasHover="false" class="card--mobile-only" />
        <FolderCard
          ref="cards" v-for="item in items" :key="item.identifier"
          :item="item" :selected="selectedKey === item.identifier" 
          @click.native="onFolderClick(item)"
          @mouseover.native="onCardMouseOver(item)"
          @mouseleave.native="onCardMouseLeave(item)"
        />
      </Slider>
    </div>
  </div>
</template>

<script>
  import cardsService, { CardColor, MediaType } from '@/services/cards.service';
  import { soundList, soundPlayer } from '@/utils/sounds';
  import { toggleFullScreen } from '@/utils/settings.fullscreen';
  import Slider from '@/components/Slider.vue';
  import Card from '@/components/Card.vue';
  import FolderCard from '@/components/FolderCard.vue';
  import Banner from '@/components/Banner.vue';
  import { Events } from '@/models/analytics';

  export default {
    components: { Slider, Card, FolderCard, Banner },
    data() {
      return {
        items: [],
        isScrolling: false,
        horizontalVisibleCardCount: 0,
        selectedKey: null,
        selectedIndex: 0,
        flagUpdatedFocus: false,
        showAuthBanner: false,
        imageDeviceType: 'phone-m',
      };
    },
    computed: {
      getPremium() {
        return this.$store.getters['account/getPremium'];
      },
      folderItem() {
        return this.$store.getters['activeItem/getActiveItem'];
      },
      imageUrl() {
        if (this.folderItem.mediaType === MediaType.MixedGames) {
          return 'https://d3aod987c9rl70.cloudfront.net/webApp/games-new/images/coloring.png';
        }

        return `${process.env.VUE_APP_CLOUDFRONT_URL}/${this.imageDeviceType}/${this.$route.params.identifier}.png`;
      },
    },
    methods: {
      setSound(val) {
        this.$store.dispatch('settings/setSound', val);
      },
      triggerScrollRefresh() {
        this.$refs.slider.$refs.scroller.refresh();
      },
      onCardMouseOver(item) {
        this.selectedKey = item.identifier;
        this.selectedIndex = this.items.findIndex(this.findIndexLoc);
      },
      findIndexLoc(element) {
        return element.identifier === this.selectedKey;
      },
      onCardMouseLeave() {
        this.selectedKey = null;
      },
      handleKeyboard(event) {
        event.preventDefault();
        const indexDelta = this.getIndexDelta(event);
        const oldIndex = this.selectedIndex || 0;

        if (this.selectedIndex === null) {
          this.selectedIndex = 0;
        } else {
          this.selectedIndex += indexDelta;
        }

        this.autoScrollIfNeeded(oldIndex, this.selectedIndex);

        this.selectedKey = this.items[this.selectedIndex].identifier;

        const item = this.getSelectedItem();

        try {
          switch (event.keyCode) {
            case VK_ENTER: {
              if (item) {
                this.onFolderClick(item);
              }
            }
          }
        } catch (error) {
          switch (event.code) {
            case 'Enter': {
              if (item) {
                this.onFolderClick(item);
              }
            }
          }
        }
      },
      getIndexDelta(event) {
        let delta = 0;
        try {
          switch (event.keyCode) {
            case VK_LEFT: {
              if (this.selectedIndex != null && this.selectedIndex > 0) {
                delta--;
              }
              break;
            }
            case VK_RIGHT: {
              if (this.selectedIndex != null && this.selectedIndex < this.items.length - 1) {
                delta++;
              }
              break;
            }
          }
        } catch (error) {
          switch (event.code) {
            case 'ArrowLeft': {
              if (this.selectedIndex != null && this.selectedIndex > 0) {
                delta--;
              }
              break;
            }
            case 'ArrowRight': {
              if (this.selectedIndex != null && this.selectedIndex < this.items.length - 1) {
                delta++;
              }
              break;
            }
          }
        }

        return delta;
      },
      getSelectedItem() {
        return this.items.find((item) => item.identifier === this.selectedKey);
      },
      getHorizontalVisibleCardCount() {
        let count = 0;
        const cards = this.$refs.cards;
        for (let i = 0; i < cards.length; i += 2) {
          if (!this.isVisibleOnScreen(cards[i].$el)) {
            break;
          }

          count++;
        }

        return count;
      },
      autoScrollIfNeeded(oldIndex, newIndex) {
        const count = this.horizontalVisibleCardCount;
        const delta = oldIndex - newIndex;
        const direction = delta === 0 ? null : delta > 0 ? 'reverse' : 'forward';
        const slider = this.$refs.slider;
        const size = this.$refs.cards && this.$refs.cards.length > 0
            ? (this.$refs.cards[0].$el).offsetWidth + 20 : 0;
        if (newIndex > count / 2 && direction === 'forward') {
          slider.scrollHorizontally(size);
        } else if (direction === 'reverse') {
          slider.scrollHorizontally(-size);
        }
      },
      isVisibleOnScreen(element) {
        const rect = element.getBoundingClientRect();
        const clientWidth = document.documentElement ? document.documentElement.clientWidth : 0;
        const viewWidth = Math.max(clientWidth, window.innerWidth);
        return !(rect.left < 0 || rect.right - viewWidth >= 0);
      },
      onScroll(scrollInfo) {
        setTimeout(() => {
          this.isScrolling = scrollInfo.isScrolling;
        }, 100);
      },
      goBack() {
        soundPlayer(soundList.cardClose);
        this.$router.push({name: 'home'});
      },
      onFolderClick(item) {
        if (this.getToken !== false && localStorage.getItem('kidj_ut')) {
          this.$store.dispatch('analytics/updateAnalyticsGameMedia', item);
          this.$store.dispatch('analytics/postAnalytics', Events.CLICK, { root: true });
        }
        if (!this.isScrolling) {
          this.setSound(false);
          if (this.canOpenFolder(item)) {
            toggleFullScreen();
            this.$router.push({
              name: 'gaming',
              params: { identifier: item.identifier, gameType: item.gameType, position: item.position },
            });
          } else {
            this.showAuthBanner = true;
          }
        }
      },
      canOpenFolder(item) {
        return !item.isLocked || this.getPremium;
      },
    },
    async created() {
      const color = this.$route.params.color;
      const folderColor = color && color !== 'white' ? color : CardColor.Red;
      const card = await cardsService.getCardFolder(this.$route.params.identifier.toString());

      if (this.folderItem.identifier === null) {
        const { imageUrl } = this;
        this.$store.dispatch('activeItem/setActiveItem', { ...card, imageUrl });
      }

      if (card != null) {
        this.items = card.subCards
          .map((subCard) => {
            return {
              identifier: subCard.identifier,
              imageUrl: subCard.identifier ? `https://d3aod987c9rl70.cloudfront.net/gameImages/cover/phone-m/${subCard.identifier}.png` : null,
              dif: subCard.dif,
              gameType: subCard.gameType,
              isLocked: subCard.isLocked,
              position: subCard.position,
              color: folderColor,
            };
          });
      }
      this.triggerScrollRefresh();
    },
    mounted() {
      soundPlayer(soundList.general);
      document.addEventListener('keyup', this.handleKeyboard);
    },
    updated() {
      // Retrieve visible cards count on screen
      if (!this.flagUpdatedFocus) {
        this.flagUpdatedFocus = true;
        this.selectedKey = this.items[0].identifier;
        const item = this.getSelectedItem();
      }
      if (this.horizontalVisibleCardCount === 0) {
        this.horizontalVisibleCardCount = this.getHorizontalVisibleCardCount();
      }
    },
    destroyed() {
      document.removeEventListener('keyup', this.handleKeyboard);
    },
  };
</script>

<style scoped lang="scss">
  @import '../assets/styles/breakpoints.scss';

  .top-bar {
    position: relative;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (orientation: portrait) {
      &--has-height {
        box-sizing: border-box;
        height: 74px;
        padding: 10px 10px 0;
      }
    }

    img {
      vertical-align: middle;
    }

    .home-btn {
      position: absolute;
      top: 10px;
      left: 10px;
      cursor: pointer;
      outline: none;

      @media (min-width: $breakpoint-lg) {
        top: 20px;
        left: 20px;
      }

      &.active {
        transform: scale(1.1);
      }

      &:hover,
      &:focus {
        transform: scale(1.1);
      }

      img {
        width: 60px;
        height: 60px;

        @media (min-width: $breakpoint-lg) {
          width: 75px;
          height: 75px;
        }
      }
    }
  }

  .card {
    &--mobile-only {
      @media (min-width: $breakpoint-lg) {
        display: none;
      }
    }

    &--desktop-only {
      display: none;
      height: 148px;
      width: 148px;
      border-width: 3px;

      @media (orientation: landscape) and (min-width: $breakpoint-lg) {
        display: block;
        position: absolute;
        top: 20px;
        left: 0;
        right: 0;
        margin: auto;
      }
    }
  }
  
  .items-wrap {
    position: relative;

    @media (orientation: portrait) {
      height: 100%;

      .top-bar--has-height + & {
        height: calc(100% - 74px);
      }
    }

    @media (orientation: landscape) {
      height: 100%;
    }
  }
</style>